import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface PerformanceData {
  picks: {
    ticker: string;
    createdAt: number;
    currentPrice: number;
    oneDayPercent: number | null;
    oneWeekPercent: number | null;
    oneMonthPercent: number | null;
    threeMonthPercent: number | null;
    sixMonthPercent: number | null;
    oneYearPercent: number | null;
  }[];
  systemAverages: {
    oneDayPercent: number | null;
    oneWeekPercent: number | null;
    oneMonthPercent: number | null;
    threeMonthPercent: number | null;
    sixMonthPercent: number | null;
    oneYearPercent: number | null;
  };
  indexes: {
    ticker: string;
    name: string;
    oneDayPercent: number | null;
    oneWeekPercent: number | null;
    oneMonthPercent: number | null;
    threeMonthPercent: number | null;
    sixMonthPercent: number | null;
    oneYearPercent: number | null;
  }[];
}

let cachedModalData: PerformanceData | null = null;
let lastModalFetchTime = 0;
const MODAL_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function TopPicksPerformanceModal({ isOpen, onClose, onAnalyzeTicker }: { isOpen: boolean; onClose: () => void; onAnalyzeTicker?: (ticker: string) => void; }) {
  const { t } = useTranslation();
  const [data, setData] = useState<PerformanceData | null>(cachedModalData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const isExpired = Date.now() - lastModalFetchTime > MODAL_CACHE_TTL;
      if (!cachedModalData || isExpired) {
        fetchData();
      } else if (cachedModalData && data !== cachedModalData) {
        setData(cachedModalData);
      }
    }
  }, [isOpen]);

  const fetchData = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/top-picks-performance${forceRefresh ? '?refresh=true' : ''}`);
      if (!res.ok) throw new Error('Failed to fetch performance data');
      const json = await res.json();
      cachedModalData = json;
      lastModalFetchTime = Date.now();
      setData(json);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatPercent = (val: number | null) => {
    if (val === null || val === undefined) return '-';
    const sign = val > 0 ? '+' : '';
    return `${sign}${val.toFixed(2)}%`;
  };

  const getPercentColor = (val: number | null) => {
    if (val === null || val === undefined) return 'text-zinc-500';
    if (val > 0) return 'text-emerald-400';
    if (val < 0) return 'text-red-400';
    return 'text-zinc-300';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-container" 
        onClick={(e) => { if(e.target === e.currentTarget) onClose(); }}
      >
        <motion.div 
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="modal-content w-full max-w-5xl max-h-[90vh] overflow-y-auto !p-4 sm:!p-6"
        >
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight uppercase">Top Picks Performance</h2>
              <p className="text-[10px] sm:text-xs text-zinc-400 uppercase tracking-widest mt-1">Comparing Top Picks against main indices across all timeframes</p>
            </div>
            <button onClick={onClose} className="p-1.5 bg-zinc-900/50 hover:bg-zinc-800 text-zinc-400 rounded-sm transition-colors">
              <X size={18} />
            </button>
          </div>

          {loading && !data && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="animate-spin text-indigo-500 mb-4" size={32} />
              <p className="text-xs text-zinc-400 uppercase tracking-widest">Compiling historical performance...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 border border-red-500/30 p-4 rounded-sm text-red-400 text-sm">
              {error}
            </div>
          )}

          {data && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[650px]">
                <thead>
                  <tr className="border-b border-zinc-800/50">
                    <th className="w-[22%] py-3 px-2 sm:px-4 text-[10px] sm:text-xs font-bold text-zinc-400 uppercase tracking-wider">Index</th>
                    <th className="w-[13%] py-3 px-1 sm:px-3 text-[10px] sm:text-xs font-bold text-zinc-400 uppercase tracking-wider text-right">1D (%)</th>
                    <th className="w-[13%] py-3 px-1 sm:px-3 text-[10px] sm:text-xs font-bold text-zinc-400 uppercase tracking-wider text-right">1W (%)</th>
                    <th className="w-[13%] py-3 px-1 sm:px-3 text-[10px] sm:text-xs font-bold text-zinc-400 uppercase tracking-wider text-right">1M (%)</th>
                    <th className="w-[13%] py-3 px-1 sm:px-3 text-[10px] sm:text-xs font-bold text-zinc-400 uppercase tracking-wider text-right">3M (%)</th>
                    <th className="w-[13%] py-3 px-1 sm:px-3 text-[10px] sm:text-xs font-bold text-zinc-400 uppercase tracking-wider text-right">6M (%)</th>
                    <th className="w-[13%] py-3 px-1 sm:px-3 pr-2 sm:pr-4 text-[10px] sm:text-xs font-bold text-zinc-400 uppercase tracking-wider text-right">1Y (%)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/30">
                  {/* Top Picks Row */}
                  <tr className="bg-indigo-900/10 hover:bg-indigo-900/20 transition-colors">
                    <td className="py-3 px-2 sm:px-4">
                      <div className="font-black text-white text-xs sm:text-base tracking-tight uppercase">Top Picks</div>
                      <div className="text-[8px] sm:text-xs text-indigo-300 font-bold uppercase tracking-widest truncate">Pro Picks</div>
                    </td>
                    <td className={`py-3 px-1 sm:px-3 text-right font-mono font-bold text-xs sm:text-base ${getPercentColor(data.systemAverages.oneDayPercent)}`}>
                      {formatPercent(data.systemAverages.oneDayPercent)}
                    </td>
                    <td className={`py-3 px-1 sm:px-3 text-right font-mono font-bold text-xs sm:text-base ${getPercentColor(data.systemAverages.oneWeekPercent)}`}>
                      {formatPercent(data.systemAverages.oneWeekPercent)}
                    </td>
                    <td className={`py-3 px-1 sm:px-3 text-right font-mono font-bold text-xs sm:text-base ${getPercentColor(data.systemAverages.oneMonthPercent)}`}>
                      {formatPercent(data.systemAverages.oneMonthPercent)}
                    </td>
                    <td className={`py-3 px-1 sm:px-3 text-right font-mono font-bold text-xs sm:text-base ${getPercentColor(data.systemAverages.threeMonthPercent)}`}>
                      {formatPercent(data.systemAverages.threeMonthPercent)}
                    </td>
                    <td className={`py-3 px-1 sm:px-3 text-right font-mono font-bold text-xs sm:text-base ${getPercentColor(data.systemAverages.sixMonthPercent)}`}>
                      {formatPercent(data.systemAverages.sixMonthPercent)}
                    </td>
                    <td className={`py-3 px-1 sm:px-3 pr-2 sm:pr-4 text-right font-mono font-bold text-xs sm:text-base ${getPercentColor(data.systemAverages.oneYearPercent)}`}>
                      {formatPercent(data.systemAverages.oneYearPercent)}
                    </td>
                  </tr>

                  {/* Main Indexes */}
                  {data.indexes.map((idx) => (
                    <tr key={idx.ticker} className="hover:bg-zinc-900/30 transition-colors">
                      <td className="py-3 px-2 sm:px-4">
                        <div className="font-bold text-zinc-200 tracking-tight text-xs sm:text-sm">{idx.ticker}</div>
                        <div className="text-[8px] sm:text-xs text-zinc-500 uppercase tracking-widest max-w-[90px] sm:max-w-none truncate">{idx.name}</div>
                      </td>
                      <td className={`py-3 px-1 sm:px-3 text-right font-mono font-medium text-xs sm:text-sm ${getPercentColor(idx.oneDayPercent)}`}>
                        {formatPercent(idx.oneDayPercent)}
                      </td>
                      <td className={`py-3 px-1 sm:px-3 text-right font-mono font-medium text-xs sm:text-sm ${getPercentColor(idx.oneWeekPercent)}`}>
                        {formatPercent(idx.oneWeekPercent)}
                      </td>
                      <td className={`py-3 px-1 sm:px-3 text-right font-mono font-medium text-xs sm:text-sm ${getPercentColor(idx.oneMonthPercent)}`}>
                        {formatPercent(idx.oneMonthPercent)}
                      </td>
                      <td className={`py-3 px-1 sm:px-3 text-right font-mono font-medium text-xs sm:text-sm ${getPercentColor(idx.threeMonthPercent)}`}>
                        {formatPercent(idx.threeMonthPercent)}
                      </td>
                      <td className={`py-3 px-1 sm:px-3 text-right font-mono font-medium text-xs sm:text-sm ${getPercentColor(idx.sixMonthPercent)}`}>
                        {formatPercent(idx.sixMonthPercent)}
                      </td>
                      <td className={`py-3 px-1 sm:px-3 pr-2 sm:pr-4 text-right font-mono font-medium text-xs sm:text-sm ${getPercentColor(idx.oneYearPercent)}`}>
                        {formatPercent(idx.oneYearPercent)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex justify-between items-center text-xs text-zinc-500">
                <span>
                  Calculated based on {data.picks.length} picks from the last 6 months.
                  <button onClick={() => setShowAll(!showAll)} className="text-indigo-400 hover:text-indigo-300 ml-2 uppercase font-bold tracking-widest border-b border-indigo-400/30 hover:border-indigo-300 transition-colors">
                    {showAll ? "Hide All Picks" : "Show All Picks"}
                  </button>
                </span>
                <button onClick={() => fetchData(true)} disabled={loading} className="flex items-center gap-1.5 hover:text-zinc-300 transition-colors disabled:opacity-50">
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                  Refresh Data
                </button>
              </div>

              {showAll && (
                <div className="mt-6 border-t border-zinc-800/50 pt-4">
                  <h3 className="text-base sm:text-lg font-black text-white mb-3 uppercase tracking-tight">Individual Pick Performance</h3>
                  <table className="w-full text-left border-collapse min-w-[650px] text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-zinc-800/50">
                        <th className="w-[22%] py-2 px-2 sm:px-4 text-[10px] sm:text-xs text-zinc-400 font-bold uppercase tracking-wider">Ticker</th>
                        <th className="w-[13%] py-2 px-1 sm:px-3 text-[10px] sm:text-xs text-zinc-400 font-bold uppercase tracking-wider text-right">1D (%)</th>
                        <th className="w-[13%] py-2 px-1 sm:px-3 text-[10px] sm:text-xs text-zinc-400 font-bold uppercase tracking-wider text-right">1W (%)</th>
                        <th className="w-[13%] py-2 px-1 sm:px-3 text-[10px] sm:text-xs text-zinc-400 font-bold uppercase tracking-wider text-right">1M (%)</th>
                        <th className="w-[13%] py-2 px-1 sm:px-3 text-[10px] sm:text-xs text-zinc-400 font-bold uppercase tracking-wider text-right">3M (%)</th>
                        <th className="w-[13%] py-2 px-1 sm:px-3 text-[10px] sm:text-xs text-zinc-400 font-bold uppercase tracking-wider text-right">6M (%)</th>
                        <th className="w-[13%] py-2 px-1 sm:px-3 pr-2 sm:pr-4 text-[10px] sm:text-xs text-zinc-400 font-bold uppercase tracking-wider text-right">1Y (%)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800/30">
                      {data.picks.map((pick, i) => (
                        <tr 
                          key={`${pick.ticker}-${i}`} 
                          className="hover:bg-zinc-900/50 transition-colors cursor-pointer group"
                          onClick={() => onAnalyzeTicker && onAnalyzeTicker(pick.ticker)}
                        >
                          <td className="py-2.5 px-2 sm:px-4 font-black text-white group-hover:text-indigo-400 transition-colors text-xs sm:text-sm tracking-tight truncate">
                            {pick.ticker}
                          </td>
                          <td className={`py-2.5 px-1 sm:px-3 text-right font-mono font-medium text-[11px] sm:text-sm ${getPercentColor(pick.oneDayPercent)}`}>
                            {formatPercent(pick.oneDayPercent)}
                          </td>
                          <td className={`py-2.5 px-1 sm:px-3 text-right font-mono font-medium text-[11px] sm:text-sm ${getPercentColor(pick.oneWeekPercent)}`}>
                            {formatPercent(pick.oneWeekPercent)}
                          </td>
                          <td className={`py-2.5 px-1 sm:px-3 text-right font-mono font-medium text-[11px] sm:text-sm ${getPercentColor(pick.oneMonthPercent)}`}>
                            {formatPercent(pick.oneMonthPercent)}
                          </td>
                          <td className={`py-2.5 px-1 sm:px-3 text-right font-mono font-medium text-[11px] sm:text-sm ${getPercentColor(pick.threeMonthPercent)}`}>
                            {formatPercent(pick.threeMonthPercent)}
                          </td>
                          <td className={`py-2.5 px-1 sm:px-3 text-right font-mono font-medium text-[11px] sm:text-sm ${getPercentColor(pick.sixMonthPercent)}`}>
                            {formatPercent(pick.sixMonthPercent)}
                          </td>
                          <td className={`py-2.5 px-1 sm:px-3 pr-2 sm:pr-4 text-right font-mono font-medium text-[11px] sm:text-sm ${getPercentColor(pick.oneYearPercent)}`}>
                            {formatPercent(pick.oneYearPercent)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
